import { useEffect, useState } from "react";

/**
 * Giỏ hàng chọn môn học theo ngành (majorId), lưu localStorage để không mất
 * khi tải lại trang hoặc điều hướng qua lại giữa banner/trang chi tiết
 * ngành. Tách ra từ `SuggestedCourses.tsx` (Sprint 35, chốt 2026-07-13) để
 * `StudyPlan.tsx` gọi 1 LẦN DUY NHẤT rồi truyền xuống làm props dùng chung
 * cho cả `SuggestedCourses` (gợi ý kỳ hiện tại) lẫn `MajorTermsCard` (mọi kỳ
 * — "học vượt") — 1 giỏ hàng, 1 lần checkout cho cả 2 nguồn chọn môn.
 *
 * `SuggestedCourses` vẫn tự gọi hook này nội bộ khi không được truyền `cart`
 * từ ngoài (dùng độc lập ở `CourseList.tsx` banner / `MajorPublicDetail.tsx`)
 * — xem `ownCart` ở đó.
 */

const CART_STORAGE_PREFIX = "savina.suggested-courses-cart";

function cartStorageKey(majorId?: number): string | null {
  return majorId != null ? `${CART_STORAGE_PREFIX}.${majorId}` : null;
}

function readStoredCart(majorId?: number): string[] {
  const key = cartStorageKey(majorId);
  if (!key) return [];
  try {
    const raw = window.localStorage.getItem(key);
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter((s): s is string => typeof s === "string") : [];
  } catch {
    return [];
  }
}

export interface CourseCart {
  selected: string[];
  toggle: (slug: string) => void;
  addAll: (slugs: string[]) => void;
  remove: (slug: string) => void;
  clear: () => void;
}

export function useCourseCart(majorId?: number): CourseCart {
  const [selected, setSelected] = useState<string[]>(() => readStoredCart(majorId));

  // Đổi ngành (banner đổi majorId, hoặc chuyển Segmented ở StudyPlan) → nạp
  // lại giỏ hàng đã lưu riêng cho ngành đó thay vì giữ giỏ của ngành cũ.
  useEffect(() => {
    setSelected(readStoredCart(majorId));
  }, [majorId]);

  useEffect(() => {
    const key = cartStorageKey(majorId);
    if (!key) return;
    try {
      window.localStorage.setItem(key, JSON.stringify(selected));
    } catch {
      // Lưu giỏ hàng chỉ là tiện ích — bỏ qua khi localStorage đầy/bị chặn (private mode).
    }
  }, [majorId, selected]);

  const toggle = (slug: string) => {
    setSelected((prev) => (prev.includes(slug) ? prev.filter((s) => s !== slug) : [...prev, slug]));
  };

  const addAll = (slugs: string[]) => {
    setSelected((prev) => Array.from(new Set([...prev, ...slugs])));
  };

  const remove = (slug: string) => {
    setSelected((prev) => prev.filter((s) => s !== slug));
  };

  const clear = () => setSelected([]);

  return { selected, toggle, addAll, remove, clear };
}
